import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, CheckCircle2, AlertTriangle, UploadCloud, FileText, X, MoreHorizontal, Pencil, UserMinus, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import {
  useEmployees, useCreateEmployee, useBulkImportEmployees,
  useUpdateEmployee, useTerminateEmployee, useDeleteEmployee,
  useChangeRequests, useDecideChangeRequest, useLinkEmployeeAccount,
  type EmploymentType, type Employee, type BulkImportResult,
} from '@/lib/employees';
import { useMyCompany } from '@/lib/companies';
import { useDepartments } from '@/lib/departments';
import { useMembers } from '@/lib/members';
import { useCurrentUser } from '@/lib/auth';
import { formatMoney } from '@/lib/currency';
import { CountryPicker } from '@/components/ui/CountryPicker';
import { CurrencyPicker } from '@/components/ui/CurrencyPicker';
import { Combobox } from '@/components/ui/Combobox';

const COUNTRY_FLAG: Record<string, string> = {
  JP: '🇯🇵', US: '🇺🇸', GB: '🇬🇧', DE: '🇩🇪', FR: '🇫🇷', ES: '🇪🇸',
  KR: '🇰🇷', CN: '🇨🇳', SG: '🇸🇬', IN: '🇮🇳', CA: '🇨🇦', AU: '🇦🇺',
};

const JP_TYPES: { value: EmploymentType; label: string }[] = [
  { value: 'seishain', label: '正社員 / Full-time JP' },
  { value: 'keiyaku', label: '契約社員 / Contract JP' },
  { value: 'gyoumu_itaku', label: '業務委託 / Outsourced JP' },
  { value: 'part_time', label: 'パート / Part-time' },
  { value: 'intern', label: 'インターン / Intern' },
];
const GLOBAL_TYPES: { value: EmploymentType; label: string }[] = [
  { value: 'fte', label: 'Full-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'intern', label: 'Intern' },
];

export function PeoplePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState<string>(params.get('country') ?? '');
  const [status, setStatus] = useState<string>('');
  const [department, setDepartment] = useState<string>(params.get('department') ?? '');
  const [view, setView] = useState<'table' | 'org'>('table');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [terminating, setTerminating] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState<Employee | null>(null);
  const employees = useEmployees({ search: search || undefined, country: country || undefined, status: status || undefined });
  const departments = useDepartments();
  const deptName = useMemo(
    () => Object.fromEntries((departments.data ?? []).map((d) => [d.id, d.name])),
    [departments.data],
  );
  const rows = useMemo(
    () => (employees.data ?? []).filter((e) => !department || e.departmentId === department),
    [employees.data, department],
  );
  const user = useCurrentUser();
  const role = user.data?.role;
  const canEdit = role === 'owner' || role === 'admin' || role === 'manager';
  const canDelete = role === 'owner' || role === 'admin';

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('people.title', { defaultValue: 'People' })}</h1>
            <p className="mt-1 text-sm text-ink-500">
              {t('people.subtitle', { defaultValue: '{{count}} active · search by name, email, kana, or romaji', count: employees.data?.length ?? 0 })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImporting(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-card text-sm font-semibold hover:bg-muted transition"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {t('people.bulkImport', { defaultValue: 'Bulk import' })}
            </button>
            <button
              onClick={() => setCreating(true)}
              className="inline-flex items-center px-4 py-2 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition"
            >
              {t('people.addEmployee', { defaultValue: '+ Add employee' })}
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <input
            type="search"
            placeholder={t('people.searchPlaceholder', { defaultValue: 'Search…' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-ink-300 rounded-lg px-3 py-2 text-sm w-72 focus:border-accent-500 focus:outline-none"
          />
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none"
          >
            <option value="">{t('people.allCountries', { defaultValue: 'All countries' })}</option>
            {Object.entries(COUNTRY_FLAG).map(([code, flag]) => (
              <option key={code} value={code}>{flag} {code}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none"
          >
            <option value="">{t('people.statusActive', { defaultValue: 'Active' })}</option>
            <option value="on_leave">{t('people.statusOnLeave', { defaultValue: 'On leave' })}</option>
            <option value="terminated">{t('people.statusTerminated', { defaultValue: 'Terminated' })}</option>
            <option value="pending">{t('people.statusPending', { defaultValue: 'Pending' })}</option>
            <option value="all">{t('people.statusAll', { defaultValue: 'All statuses' })}</option>
          </select>
          {(departments.data ?? []).length > 0 && (
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="border border-ink-300 rounded-lg px-3 py-2 text-sm focus:border-accent-500 focus:outline-none"
            >
              <option value="">{t('people.allDepartments', { defaultValue: 'All departments' })}</option>
              {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <div className="ms-auto inline-flex rounded-lg border border-ink-300 overflow-hidden">
            <button
              onClick={() => setView('table')}
              className={`px-3 py-2 text-xs font-semibold transition ${view === 'table' ? 'bg-accent-500 text-white' : 'text-ink-600 hover:bg-ink-50'}`}
            >
              {t('people.viewTable', { defaultValue: 'Table' })}
            </button>
            <button
              onClick={() => setView('org')}
              className={`px-3 py-2 text-xs font-semibold transition ${view === 'org' ? 'bg-accent-500 text-white' : 'text-ink-600 hover:bg-ink-50'}`}
            >
              {t('people.viewOrgChart', { defaultValue: 'Org chart' })}
            </button>
          </div>
        </div>

        {canEdit && <ChangeRequestsPanel />}

        {view === 'org' ? (
          <OrgChart employees={employees.data ?? []} deptName={deptName} />
        ) : (
        <div className="mt-6 rounded-2xl border border-ink-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-ink-600">
                <tr>
                  <th className="text-start font-semibold px-4 py-2.5">{t('people.colName', { defaultValue: 'Name' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('people.colCountry', { defaultValue: 'Country' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('people.colType', { defaultValue: 'Type' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('people.colTitle', { defaultValue: 'Title' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('people.colDepartment', { defaultValue: 'Department' })}</th>
                  <th className="text-start font-semibold px-4 py-2.5">{t('people.colHireDate', { defaultValue: 'Hire date' })}</th>
                  <th className="text-end font-semibold px-4 py-2.5">{t('people.colSalary', { defaultValue: 'Salary' })}</th>
                  <th className="text-end font-semibold px-4 py-2.5">{t('people.colStatus', { defaultValue: 'Status' })}</th>
                  <th className="px-2 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {employees.isLoading && (
                  <tr><td colSpan={9} className="text-center text-ink-500 py-10">{t('people.loading', { defaultValue: 'Loading…' })}</td></tr>
                )}
                {employees.data?.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-ink-500 py-10">{t('people.emptyState', { defaultValue: 'No employees yet. Click "Add employee" to start.' })}</td></tr>
                )}
                {rows.map((e) => (
                  <Row
                    key={e.id}
                    e={e}
                    departmentName={e.departmentId ? deptName[e.departmentId] : undefined}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onEdit={() => setEditing(e)}
                    onTerminate={() => setTerminating(e)}
                    onDelete={() => setDeleting(e)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>

      {creating && <AddEmployeeDialog onClose={() => setCreating(false)} />}
      {importing && <BulkImportDialog onClose={() => setImporting(false)} />}
      {editing && <EditEmployeeDialog employee={editing} onClose={() => setEditing(null)} />}
      {terminating && <TerminateEmployeeDialog employee={terminating} onClose={() => setTerminating(null)} />}
      {deleting && <DeleteEmployeeDialog employee={deleting} onClose={() => setDeleting(null)} />}
    </AppShell>
  );
}

/**
 * Reporting-hierarchy org chart built from manager_id, labelled with each
 * person's department. Roots are people with no manager (or whose manager
 * isn't in the current set). Rendered as an indented tree with connector
 * lines; a visited-set guards against accidental manager cycles.
 */
function OrgChart({ employees, deptName }: { employees: Employee[]; deptName: Record<string, string> }) {
  const { t } = useTranslation();
  const byId = new Map(employees.map((e) => [e.id, e]));
  const childrenOf = new Map<string, Employee[]>();
  const roots: Employee[] = [];
  for (const e of employees) {
    const mgr = e.managerId && byId.has(e.managerId) ? e.managerId : null;
    if (mgr) {
      if (!childrenOf.has(mgr)) childrenOf.set(mgr, []);
      childrenOf.get(mgr)!.push(e);
    } else {
      roots.push(e);
    }
  }

  return (
    <div className="mt-6 rounded-2xl border border-ink-200 bg-white p-6 overflow-x-auto">
      {employees.length === 0 ? (
        <p className="text-sm text-ink-500 text-center py-6">{t('people.orgChartEmpty', { defaultValue: 'No employees to chart yet.' })}</p>
      ) : (
        <ul className="space-y-2 min-w-max">
          {roots.map((r) => (
            <OrgNode key={r.id} e={r} childrenOf={childrenOf} deptName={deptName} visited={new Set()} />
          ))}
        </ul>
      )}
    </div>
  );
}

function OrgNode({
  e, childrenOf, deptName, visited,
}: {
  e: Employee;
  childrenOf: Map<string, Employee[]>;
  deptName: Record<string, string>;
  visited: Set<string>;
}) {
  if (visited.has(e.id)) return null;
  const next = new Set(visited); next.add(e.id);
  const reports = childrenOf.get(e.id) ?? [];
  const dept = e.departmentId ? deptName[e.departmentId] : null;
  return (
    <li>
      <Link
        to={`/app/people/${e.id}`}
        className="inline-flex flex-col rounded-xl border border-ink-200 bg-ink-50/50 hover:border-accent-300 hover:bg-accent-50 transition px-3 py-2 min-w-[200px]"
      >
        <span className="text-sm font-semibold text-ink-900">{e.preferredName || e.legalNameRoman || e.legalNameLocal}</span>
        <span className="text-[11px] text-ink-500">
          {e.jobTitle || labelType(e.employmentType)}
          {dept ? ` · ${dept}` : ''} · {COUNTRY_FLAG[e.countryCode] ?? ''}{e.countryCode}
        </span>
      </Link>
      {reports.length > 0 && (
        <ul className="ms-5 mt-2 ps-4 border-s-2 border-ink-200 space-y-2">
          {reports.map((r) => (
            <OrgNode key={r.id} e={r} childrenOf={childrenOf} deptName={deptName} visited={next} />
          ))}
        </ul>
      )}
    </li>
  );
}

interface RowProps {
  e: Employee;
  departmentName?: string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onTerminate: () => void;
  onDelete: () => void;
}

function Row({ e, departmentName, canEdit, canDelete, onEdit, onTerminate, onDelete }: RowProps) {
  return (
    <tr className="border-t border-ink-100 hover:bg-ink-50/40">
      <td className="px-4 py-3">
        <Link to={`/app/people/${e.id}`} className="font-medium text-ink-900 hover:text-accent-600 hover:underline">
          {e.legalNameLocal}
        </Link>
        {e.legalNameRoman && e.legalNameRoman !== e.legalNameLocal && (
          <p className="text-[11px] text-ink-500">{e.legalNameRoman}</p>
        )}
        <p className="text-[11px] text-ink-500">{e.email}</p>
      </td>
      <td className="px-4 py-3">{COUNTRY_FLAG[e.countryCode] ?? ''} <span className="text-ink-700">{e.countryCode}</span></td>
      <td className="px-4 py-3 text-ink-700">{labelType(e.employmentType)}</td>
      <td className="px-4 py-3 text-ink-700">{e.jobTitle || '—'}</td>
      <td className="px-4 py-3 text-ink-700">{departmentName ?? '—'}</td>
      <td className="px-4 py-3 num text-ink-700">{e.hireDate.toString().slice(0, 10)}</td>
      <td className="px-4 py-3 num text-end text-ink-900">
        {e.baseSalaryCents != null && e.salaryCurrency
          ? `${formatMoney(e.baseSalaryCents / 100, { currency: e.salaryCurrency, maximumFractionDigits: 0 })} / ${e.salaryPeriod}`
          : '—'}
      </td>
      <td className="px-4 py-3 text-end">
        <StatusPill status={e.status} />
      </td>
      <td className="px-2 py-3 text-end">
        {canEdit && (
          <RowActionsMenu
            employee={e}
            canDelete={canDelete}
            onEdit={onEdit}
            onTerminate={onTerminate}
            onDelete={onDelete}
          />
        )}
      </td>
    </tr>
  );
}

function RowActionsMenu({
  employee, canDelete, onEdit, onTerminate, onDelete,
}: {
  employee: Employee;
  canDelete: boolean;
  onEdit: () => void;
  onTerminate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; placement: 'bottom' | 'top' } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Compute fixed-position coords from the trigger's bounding rect.
  // The menu lives in <body> via `position: fixed` so the table's
  // `overflow-hidden` wrapper can't clip it. Flips above the trigger
  // when there isn't enough room below.
  const place = () => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const MENU_W = 224;            // matches `w-56`
    const MENU_H_EST = 160;        // generous — actual height adapts
    const VW = window.innerWidth;
    const VH = window.innerHeight;
    const spaceBelow = VH - r.bottom;
    const placement: 'bottom' | 'top' = spaceBelow >= MENU_H_EST ? 'bottom' : 'top';
    const top = placement === 'bottom' ? r.bottom + 6 : r.top - MENU_H_EST - 6;
    // Right-align the menu to the trigger, clamped to the viewport.
    const left = Math.max(8, Math.min(VW - MENU_W - 8, r.right - MENU_W));
    setPos({ top, left, placement });
  };

  useEffect(() => {
    if (!open) return;
    place();
    const onDown = (ev: MouseEvent) => {
      const t = ev.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(t) &&
        menuRef.current && !menuRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const alreadyTerminated = employee.status === 'terminated';
  const deletable = canDelete && employee.canHardDelete;
  const deleteTooltip = !canDelete
    ? t('people.deleteTooltipOwnersAdmins', { defaultValue: 'Owners or admins only' })
    : !employee.canHardDelete
      ? t('people.deleteTooltipHasHistory', { defaultValue: 'This employee has payroll, document or tax history — Terminate instead' })
      : '';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-ink-100 text-ink-600 transition"
        aria-label={t('people.rowActions', { defaultValue: 'Row actions' })}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 224 }}
          className="z-50 rounded-xl border border-ink-200 bg-white shadow-card-lift py-1 text-sm"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onEdit(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-start text-ink-800 hover:bg-ink-50"
          >
            <Pencil className="h-3.5 w-3.5 text-ink-500" />
            {t('people.menuEditEmployee', { defaultValue: 'Edit employee' })}
          </button>
          {!alreadyTerminated && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onTerminate(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-start text-amber-700 hover:bg-amber-50"
            >
              <UserMinus className="h-3.5 w-3.5" />
              {t('people.menuTerminate', { defaultValue: 'Terminate…' })}
            </button>
          )}
          <div className="my-1 h-px bg-ink-100" />
          <button
            type="button"
            role="menuitem"
            disabled={!deletable}
            onClick={() => { if (deletable) { setOpen(false); onDelete(); } }}
            title={deleteTooltip || undefined}
            className={`w-full flex items-start gap-2 px-3 py-2 text-start transition ${
              deletable
                ? 'text-rose-700 hover:bg-rose-50'
                : 'text-ink-400 cursor-not-allowed'
            }`}
          >
            <Trash2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="flex-1">
              <span className="block">{t('people.menuDeletePermanently', { defaultValue: 'Delete permanently' })}</span>
              {!deletable && deleteTooltip && (
                <span className="block text-[10px] text-ink-400 mt-0.5 leading-tight">{deleteTooltip}</span>
              )}
            </span>
          </button>
        </div>
      )}
    </>
  );
}

function labelType(t: EmploymentType): string {
  switch (t) {
    case 'seishain': return '正社員';
    case 'keiyaku': return '契約社員';
    case 'gyoumu_itaku': return '業務委託';
    case 'fte': return 'Full-time';
    case 'contractor': return 'Contractor';
    case 'part_time': return 'Part-time';
    case 'intern': return 'Intern';
  }
}

function StatusPill({ status }: { status: Employee['status'] }) {
  const map: Record<Employee['status'], { cls: string; label: string }> = {
    active:     { cls: 'bg-ok-50 text-ok-600',     label: '● Active' },
    pending:    { cls: 'bg-ink-100 text-ink-500',  label: '○ Pending' },
    on_leave:   { cls: 'bg-warn-50 text-warn-600', label: '◐ On leave' },
    terminated: { cls: 'bg-ink-100 text-ink-400 line-through', label: '✕ Terminated' },
  };
  const m = map[status];
  return <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ${m.cls}`}>{m.label}</span>;
}

const SAMPLE_CSV = `name,email,country,hire_date,type,salary,currency
山田太郎,taro@example.com,JP,2026-04-01,seishain,450000,JPY
Alice Chen,alice@example.com,US,2026-04-01,fte,8000,USD`;

const MAX_CSV_BYTES = 5 * 1024 * 1024; // 5 MB — keeps the textarea-paste path useful too

function downloadTemplate() {
  // BOM-prefixed so Excel detects UTF-8 (matters for 山田太郎 etc.).
  const blob = new Blob(['﻿' + SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'keenez-employees-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function BulkImportDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [pasteMode, setPasteMode] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const importMut = useBulkImportEmployees();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const readFile = async (file: File) => {
    setFileError(null);
    const isCsv =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLowerCase().endsWith('.csv') ||
      file.name.toLowerCase().endsWith('.tsv') ||
      file.name.toLowerCase().endsWith('.txt');
    if (!isCsv) {
      setFileError(t('people.bulkImportFileTypeError', { defaultValue: 'Please upload a .csv file (Excel: File → Save As → CSV UTF-8).' }));
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setFileError(t('people.bulkImportFileSizeError', { defaultValue: 'File is too large — keep it under 5 MB. Split into multiple imports if needed.' }));
      return;
    }
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
  };

  const clearFile = () => {
    setCsv('');
    setFileName(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void readFile(file);
  };

  const onRun = async () => {
    if (!csv.trim()) return;
    const r = await importMut.mutateAsync(csv);
    setResult(r);
  };

  const rowCount = csv ? Math.max(0, csv.trim().split(/\r?\n/).length - 1) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center px-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6 max-h-[85vh] flex flex-col">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('people.bulkImportTitle')}</p>
          <h2 className="text-lg font-semibold mt-1">{t('people.bulkImportTitle')}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t('people.bulkImportBody')}</p>
        </header>

        {!result ? (
          <>
            {!pasteMode ? (
              <div className="mt-4 flex flex-col gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.tsv,.txt,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void readFile(f);
                  }}
                />
                {!fileName ? (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                    className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition ${
                      dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-muted/40'
                    }`}
                  >
                    <UploadCloud className="h-8 w-8 text-primary" />
                    <p className="text-sm font-semibold">
                      {t('people.bulkImportDropTitle', { defaultValue: 'Drop your CSV here or click to choose' })}
                    </p>
                    <p className="text-[11px] text-muted-foreground max-w-md">
                      {t('people.bulkImportDropHint', { defaultValue: 'CSV with header row · UTF-8 · up to 5 MB. Required columns: name, email, country, hire_date.' })}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-5 w-5 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate">{fileName}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {t('people.bulkImportRowCount', { count: rowCount, defaultValue: `${rowCount} row${rowCount === 1 ? '' : 's'} detected` })}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearFile}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted transition"
                        aria-label={t('common.clear', { defaultValue: 'Clear' })}
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                    <pre className="mt-3 max-h-32 overflow-auto text-[10px] leading-relaxed text-muted-foreground bg-muted/40 rounded-md p-2 font-mono whitespace-pre">{csv.split(/\r?\n/).slice(0, 6).join('\n')}{csv.split(/\r?\n/).length > 6 ? '\n…' : ''}</pre>
                  </div>
                )}
                {fileError && (
                  <p className="text-xs text-rose-600 flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {fileError}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  <button type="button" onClick={downloadTemplate} className="text-primary hover:underline">
                    {t('people.bulkImportTemplate', { defaultValue: 'Download CSV template' })}
                  </button>
                  <span className="text-muted-foreground/40">·</span>
                  <button type="button" onClick={() => setPasteMode(true)} className="hover:underline">
                    {t('people.bulkImportPaste', { defaultValue: 'Or paste CSV text instead' })}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <textarea
                  value={csv}
                  onChange={(e) => { setCsv(e.target.value); setFileName(null); }}
                  placeholder={SAMPLE_CSV}
                  rows={10}
                  className="mt-4 flex-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-xs font-mono outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
                />
                <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{t('people.bulkImportRowCount', { count: rowCount, defaultValue: `${rowCount} row${rowCount === 1 ? '' : 's'} detected` })}</span>
                  <button type="button" onClick={() => { setPasteMode(false); clearFile(); }} className="text-primary hover:underline">
                    {t('people.bulkImportBackToFile', { defaultValue: '← Back to file upload' })}
                  </button>
                </div>
              </>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="inline-flex h-10 items-center px-4 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition">
                {t('people.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={onRun}
                disabled={importMut.isPending || !csv.trim()}
                className="gradient-primary inline-flex h-10 items-center gap-2 px-5 rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60"
              >
                {importMut.isPending ? <span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                {t('people.bulkImportRun')}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-4 flex-1 overflow-y-auto keenez-scrollbar">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              {t('people.bulkImportSummary', { ok: result.created.length, skipped: result.skipped.length })}
            </div>
            {result.skipped.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs">
                {result.skipped.map((s) => (
                  <li key={s.row} className="flex items-start gap-1.5 text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{t('people.bulkImportSkippedRow', { defaultValue: 'Row {{row}}: {{reason}}', row: s.row, reason: s.reason })}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex justify-end">
              <button onClick={onClose} className="gradient-primary inline-flex h-10 items-center px-5 rounded-xl text-white text-sm font-semibold shadow-glow">
                {t('people.done', { defaultValue: 'Done' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddEmployeeDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const company = useMyCompany();
  const create = useCreateEmployee();
  const departments = useDepartments();
  const colleagues = useEmployees({});
  const isJp = (company.data?.hqCountryCode === 'JP');

  const [legalNameLocal, setLegalNameLocal] = useState('');
  const [legalNameRoman, setLegalNameRoman] = useState('');
  const [legalNameKana, setLegalNameKana] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(company.data?.hqCountryCode ?? 'JP');
  const [type, setType] = useState<EmploymentType>(isJp ? 'seishain' : 'fte');
  const [hireDate, setHireDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [jobTitle, setJobTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [baseSalary, setBaseSalary] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // Set when the backend returns 402 PAYMENT_REQUIRED on
  // SeatLimitReached / CountryLimitReached. Renders a structured
  // upgrade card with a "Manage subscription" link instead of the
  // plain-text error box.
  const [planBlock, setPlanBlock] = useState<{ reason: string; message: string; requiredPlan: string } | null>(null);

  const types = country === 'JP' ? JP_TYPES : GLOBAL_TYPES;
  const isCountryJp = country === 'JP';
  const salaryCurrency = isCountryJp ? 'JPY' : country === 'GB' ? 'GBP' : country === 'DE' || country === 'FR' || country === 'ES' ? 'EUR' : 'USD';
  const salaryPeriod = isCountryJp ? 'monthly' : 'annual';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPlanBlock(null);
    try {
      await create.mutateAsync({
        legalNameLocal: legalNameLocal.trim(),
        legalNameKana: legalNameKana.trim() || undefined,
        legalNameRoman: legalNameRoman.trim() || undefined,
        email: email.trim().toLowerCase(),
        countryCode: country,
        employmentType: type,
        hireDate,
        jobTitle: jobTitle.trim() || undefined,
        departmentId: departmentId || undefined,
        managerId: managerId || undefined,
        baseSalaryCents: baseSalary ? Math.round(Number(baseSalary) * 100) : undefined,
        salaryCurrency: baseSalary ? salaryCurrency : undefined,
        salaryPeriod: baseSalary ? (salaryPeriod as 'monthly' | 'annual') : undefined,
      });
      onClose();
    } catch (err: unknown) {
      const r = (err as { response?: { status?: number; data?: { error?: string; message?: string | string[]; requiredPlan?: string } } })?.response;
      const data = r?.data;
      if (r?.status === 402 && data?.error && (data.error === 'SeatLimitReached' || data.error === 'CountryLimitReached')) {
        setPlanBlock({
          reason: data.error,
          message: Array.isArray(data.message) ? data.message[0] : (data.message ?? ''),
          requiredPlan: data.requiredPlan ?? 'growth',
        });
        return;
      }
      const msg = data?.message
        || (err as { message?: string })?.message
        || t('people.errorCreate', { defaultValue: 'Failed to create employee' });
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-lg w-full p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold text-ink-900">{t('people.addEmployeeTitle', { defaultValue: 'Add employee' })}</h2>
        <p className="text-xs text-ink-500">{t('people.piiNote', { defaultValue: 'PII (マイナンバー, SSN, bank account) is collected later via the secure form, not here.' })}</p>

        <Field label={isCountryJp ? t('people.fieldLocalName', { defaultValue: '氏名 (Local name)' }) : t('people.fieldLegalName', { defaultValue: 'Legal name' })} required>
          <input type="text" required value={legalNameLocal} onChange={(e) => setLegalNameLocal(e.target.value)} className="input" placeholder={isCountryJp ? '山田 太郎' : 'Taro Yamada'} />
        </Field>

        {isCountryJp && (
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('people.fieldFurigana', { defaultValue: 'フリガナ' })} optional>
              <input type="text" value={legalNameKana} onChange={(e) => setLegalNameKana(e.target.value)} className="input" placeholder="ヤマダ タロウ" />
            </Field>
            <Field label={t('people.fieldRomaji', { defaultValue: 'Romaji' })} optional>
              <input type="text" value={legalNameRoman} onChange={(e) => setLegalNameRoman(e.target.value)} className="input" placeholder="Taro Yamada" />
            </Field>
          </div>
        )}

        <Field label={t('people.fieldWorkEmail', { defaultValue: 'Work email' })} required>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" placeholder="taro@acme.com" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('people.fieldCountry', { defaultValue: 'Country' })} required>
            <CountryPicker value={country} onChange={setCountry} />
          </Field>
          <Field label={t('people.fieldEmploymentType', { defaultValue: 'Employment type' })} required>
            <Combobox<EmploymentType>
              value={type}
              onChange={setType}
              items={types.map((t) => ({ value: t.value, label: t.label, badge: t.value }))}
              hideSearch
              align="left"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('people.fieldHireDate', { defaultValue: 'Hire date' })} required>
            <input type="date" required value={hireDate} onChange={(e) => setHireDate(e.target.value)} className="input" />
          </Field>
          <Field label={t('people.fieldJobTitle', { defaultValue: 'Job title' })} optional>
            <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="input" placeholder={t('people.jobTitlePlaceholder', { defaultValue: 'Senior Engineer' })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('people.fieldDepartment', { defaultValue: 'Department' })} optional>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="input">
              <option value="">—</option>
              {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label={t('people.fieldManager', { defaultValue: 'Manager' })} optional>
            <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="input">
              <option value="">—</option>
              {(colleagues.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.preferredName || m.legalNameLocal}</option>)}
            </select>
          </Field>
        </div>

        <Field label={t('people.fieldBaseSalaryWithCurrency', { defaultValue: 'Base salary ({{currency}}, {{period}})', currency: salaryCurrency, period: salaryPeriod })} optional>
          <input
            type="number"
            min="0"
            step={isCountryJp ? 1000 : 100}
            value={baseSalary}
            onChange={(e) => setBaseSalary(e.target.value)}
            className="input"
            placeholder={isCountryJp ? '450000' : '120000'}
          />
        </Field>

        {planBlock && (
          <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-4">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-amber-200 text-amber-800 font-bold text-base shrink-0">!</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {planBlock.reason === 'CountryLimitReached'
                    ? t('people.planBlockMultiCountry', { defaultValue: 'Multi-country plan required' })
                    : t('people.planBlockSeatLimit', { defaultValue: 'Seat limit reached on Free plan' })}
                </p>
                <p className="mt-1 text-xs text-amber-800">{planBlock.message}</p>
                <Link
                  to="/app/settings"
                  onClick={onClose}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-700 transition"
                >
                  {t('people.manageSubscription', { defaultValue: 'Manage subscription →' })}
                </Link>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('people.cancel', { defaultValue: 'Cancel' })}</button>
          <button type="submit" disabled={!legalNameLocal.trim() || !email.trim() || create.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">
            {create.isPending ? t('people.saving', { defaultValue: 'Saving…' }) : t('people.saveEmployee', { defaultValue: 'Save employee' })}
          </button>
        </div>

      </form>
    </div>
  );
}

function Field({ label, required, optional, children }: { label: string; required?: boolean; optional?: boolean; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <label className="block min-w-0">
      <span className="block text-xs font-medium text-ink-700 mb-1">
        {label}
        {required && <span className="text-warn-500 ms-1">*</span>}
        {optional && <span className="text-ink-400 ms-2 font-normal">{t('people.optional', { defaultValue: 'optional' })}</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * Edit dialog — mirrors AddEmployeeDialog shape but only edits the
 * mutable subset (PATCH /employees/:id). Country and employment-type
 * stay locked because changing those mid-employment ripples into
 * payroll-engine routing; v1.1 will offer a guided "transfer" flow.
 */
function EditEmployeeDialog({ employee, onClose }: { employee: Employee; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateEmployee();
  const departments = useDepartments();
  const colleagues = useEmployees({});
  const members = useMembers();
  const linkAccount = useLinkEmployeeAccount();
  const [linkedUserId, setLinkedUserId] = useState(employee.userId ?? '');
  const isCountryJp = employee.countryCode === 'JP';

  const [preferredName, setPreferredName] = useState(employee.preferredName ?? '');
  const [email, setEmail] = useState(employee.email);
  const [jobTitle, setJobTitle] = useState(employee.jobTitle ?? '');
  const [departmentId, setDepartmentId] = useState(employee.departmentId ?? '');
  const [managerId, setManagerId] = useState(employee.managerId ?? '');
  const [baseSalary, setBaseSalary] = useState<string>(
    employee.baseSalaryCents != null ? String(employee.baseSalaryCents / 100) : '',
  );
  const [salaryCurrency, setSalaryCurrency] = useState(employee.salaryCurrency ?? (isCountryJp ? 'JPY' : 'USD'));
  const [salaryPeriod, setSalaryPeriod] = useState<'monthly' | 'annual' | 'hourly'>(employee.salaryPeriod);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        id: employee.id,
        preferredName: preferredName.trim() || undefined,
        email: email.trim().toLowerCase(),
        jobTitle: jobTitle.trim() || undefined,
        departmentId: departmentId || undefined,
        managerId: managerId || undefined,
        baseSalaryCents: baseSalary ? Math.round(Number(baseSalary) * 100) : undefined,
        salaryCurrency: baseSalary ? salaryCurrency : undefined,
        salaryPeriod: baseSalary ? salaryPeriod : undefined,
      });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || t('people.errorUpdate', { defaultValue: 'Failed to update employee' });
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-2xl w-full p-6 space-y-4"
      >
        <header>
          <h2 className="text-lg font-semibold text-ink-900">{t('people.editEmployeeTitle', { defaultValue: 'Edit employee' })}</h2>
          <p className="text-xs text-ink-500 mt-1">
            {employee.legalNameLocal}
            {employee.legalNameRoman && employee.legalNameRoman !== employee.legalNameLocal && ` · ${employee.legalNameRoman}`}
          </p>
          <p className="text-[11px] text-ink-400 mt-1">
            {t('people.editImmutableNote', { defaultValue: 'Country and employment-type are immutable. To move someone across borders, terminate and re-hire (or wait for the v1.1 transfer flow).' })}
          </p>
        </header>

        <Field label={t('people.fieldPreferredName', { defaultValue: 'Preferred name' })} optional>
          <input type="text" value={preferredName} onChange={(e) => setPreferredName(e.target.value)} className="input" placeholder={t('people.preferredNamePlaceholder', { defaultValue: 'What they go by' })} />
        </Field>

        <Field label={t('people.fieldWorkEmail', { defaultValue: 'Work email' })} required>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
        </Field>

        <Field label={t('people.fieldJobTitle', { defaultValue: 'Job title' })} optional>
          <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="input" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('people.fieldDepartment', { defaultValue: 'Department' })} optional>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className="input">
              <option value="">—</option>
              {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label={t('people.fieldManager', { defaultValue: 'Manager' })} optional>
            <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="input">
              <option value="">—</option>
              {(colleagues.data ?? []).filter((m) => m.id !== employee.id).map((m) => <option key={m.id} value={m.id}>{m.preferredName || m.legalNameLocal}</option>)}
            </select>
          </Field>
        </div>

        <Field label={t('people.fieldLinkedAccount', { defaultValue: 'Linked account (self-service login)' })} optional>
          <select
            value={linkedUserId}
            onChange={(e) => { setLinkedUserId(e.target.value); linkAccount.mutate({ id: employee.id, userId: e.target.value || null }); }}
            className="input"
          >
            <option value="">{t('people.notLinked', { defaultValue: '— not linked —' })}</option>
            {(members.data?.members ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.displayName} ({m.email})</option>
            ))}
          </select>
          <p className="text-[10px] text-ink-400 mt-1">{t('people.linkedAccountHelp', { defaultValue: 'Links this employee to a member account so they can view their record and submit self-service change requests.' })}</p>
        </Field>

        <div className="grid grid-cols-[1fr_1.3fr_1fr] gap-3">
          <Field label={t('people.fieldBaseSalary', { defaultValue: 'Base salary' })} optional>
            <input
              type="number"
              min="0"
              step={isCountryJp ? 1000 : 100}
              value={baseSalary}
              onChange={(e) => setBaseSalary(e.target.value)}
              className="input"
            />
          </Field>
          <Field label={t('people.fieldCurrency', { defaultValue: 'Currency' })} optional>
            <CurrencyPicker value={salaryCurrency} onChange={setSalaryCurrency} />
          </Field>
          <Field label={t('people.fieldPeriod', { defaultValue: 'Period' })} optional>
            <select value={salaryPeriod} onChange={(e) => setSalaryPeriod(e.target.value as 'monthly' | 'annual' | 'hourly')} className="input">
              <option value="monthly">{t('people.periodMonthly', { defaultValue: 'Monthly' })}</option>
              <option value="annual">{t('people.periodAnnual', { defaultValue: 'Annual' })}</option>
              <option value="hourly">{t('people.periodHourly', { defaultValue: 'Hourly' })}</option>
            </select>
          </Field>
        </div>

        {error && (
          <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('people.cancel', { defaultValue: 'Cancel' })}</button>
          <button type="submit" disabled={!email.trim() || update.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">
            {update.isPending ? t('people.saving', { defaultValue: 'Saving…' }) : t('people.saveChanges', { defaultValue: 'Save changes' })}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Terminate dialog — collects effective end-date + optional reason,
 * then POSTs to /employees/:id/terminate. Record is kept forever for
 * tax/legal retention (status='terminated', termination_date set).
 */
function TerminateEmployeeDialog({ employee, onClose }: { employee: Employee; onClose: () => void }) {
  const { t } = useTranslation();
  const terminate = useTerminateEmployee();
  const [terminationDate, setTerminationDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await terminate.mutateAsync({ id: employee.id, terminationDate, reason: reason.trim() || undefined });
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
        || (err as { message?: string })?.message
        || t('people.errorTerminate', { defaultValue: 'Failed to terminate employee' });
      setError(Array.isArray(msg) ? msg[0] : msg);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-glow border border-amber-200 max-w-md w-full p-6 space-y-4"
      >
        <header>
          <div className="flex items-center gap-2 text-amber-700">
            <UserMinus className="h-5 w-5" />
            <h2 className="text-lg font-semibold">{t('people.terminateTitle', { defaultValue: 'Terminate employee' })}</h2>
          </div>
          <p className="text-sm text-ink-600 mt-2">
            <strong>{employee.legalNameLocal}</strong> {t('people.terminateWarning', { defaultValue: 'will be marked as terminated. Their payroll, document, and audit records are preserved for legal retention.' })}
          </p>
        </header>

        <Field label={t('people.fieldTerminationDate', { defaultValue: 'Termination date' })} required>
          <input type="date" required value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} className="input" />
        </Field>

        <Field label={t('people.fieldReason', { defaultValue: 'Reason' })} optional>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="input"
            placeholder={t('people.reasonPlaceholder', { defaultValue: 'Resignation, end of contract, etc.' })}
          />
        </Field>

        {error && (
          <div className="rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('people.cancel', { defaultValue: 'Cancel' })}</button>
          <button type="submit" disabled={terminate.isPending} className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition">
            {terminate.isPending ? t('people.terminating', { defaultValue: 'Terminating…' }) : t('people.confirmTermination', { defaultValue: 'Confirm termination' })}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Hard-delete confirm dialog. Only reachable when the row reports
 * `canHardDelete=true`, but we still surface the API's structured
 * 409 if the state changed between page load and click (race).
 */
function DeleteEmployeeDialog({ employee, onClose }: { employee: Employee; onClose: () => void }) {
  const { t } = useTranslation();
  const del = useDeleteEmployee();
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, boolean> | null>(null);

  async function submit() {
    setError(null);
    setReasons(null);
    try {
      await del.mutateAsync(employee.id);
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string | string[]; reasons?: Record<string, boolean> } } })?.response?.data;
      const msg = data?.message
        || (err as { message?: string })?.message
        || t('people.errorDelete', { defaultValue: 'Failed to delete employee' });
      setError(Array.isArray(msg) ? msg[0] : msg);
      if (data?.reasons) setReasons(data.reasons);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-glow border border-rose-200 max-w-md w-full p-6 space-y-4"
      >
        <header>
          <div className="flex items-center gap-2 text-rose-700">
            <Trash2 className="h-5 w-5" />
            <h2 className="text-lg font-semibold">{t('people.deleteTitle', { defaultValue: 'Delete permanently?' })}</h2>
          </div>
          <p className="text-sm text-ink-700 mt-3">
            <strong>{employee.legalNameLocal}</strong> ({employee.email}) {t('people.deleteWarning', { defaultValue: 'will be erased from the database. This cannot be undone.' })}
          </p>
          <p className="text-xs text-ink-500 mt-2">
            {t('people.deleteConstraintNote', { defaultValue: 'This is only allowed because no payroll runs, signed documents, tax records, performance reviews, or reports reference this employee. If any of that changes later, use Terminate instead.' })}
          </p>
        </header>

        {error && (
          <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">
            {error}
            {reasons && (
              <ul className="mt-2 list-disc list-inside text-xs">
                {reasons.has_payroll && <li>{t('people.reasonHasPayroll', { defaultValue: 'Has payroll history' })}</li>}
                {reasons.has_documents && <li>{t('people.reasonHasDocuments', { defaultValue: 'Has signed documents' })}</li>}
                {reasons.has_nencho && <li>{t('people.reasonHasNencho', { defaultValue: 'Has year-end tax records (年末調整)' })}</li>}
                {reasons.has_performance && <li>{t('people.reasonHasPerformance', { defaultValue: 'Has performance reviews' })}</li>}
                {reasons.has_reports && <li>{t('people.reasonHasReports', { defaultValue: 'Manages other employees' })}</li>}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('people.cancel', { defaultValue: 'Cancel' })}</button>
          <button
            type="button"
            onClick={submit}
            disabled={del.isPending}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 transition"
          >
            {del.isPending ? t('people.deleting', { defaultValue: 'Deleting…' }) : t('people.menuDeletePermanently', { defaultValue: 'Delete permanently' })}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Admin/manager review of employee self-service change requests (申請). */
function ChangeRequestsPanel() {
  const { t } = useTranslation();
  const reqs = useChangeRequests('pending');
  const decide = useDecideChangeRequest();
  const employees = useEmployees({});
  const list = reqs.data ?? [];
  if (list.length === 0) return null;
  const nameOf = (id: string) => employees.data?.find((e) => e.id === id)?.legalNameLocal ?? id.slice(0, 8);
  return (
    <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/40 overflow-hidden">
      <div className="px-5 py-3 border-b border-amber-200 bg-amber-50">
        <h2 className="text-sm font-semibold text-amber-800">{t('people.pendingChangeRequests', { defaultValue: 'Pending change requests ({{count}})', count: list.length })}</h2>
      </div>
      <ul className="divide-y divide-amber-100">
        {list.map((cr) => (
          <li key={cr.id} className="px-5 py-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink-900">{nameOf(cr.employeeId)}</p>
              <p className="text-xs text-ink-600">
                {Object.entries(cr.changes).map(([k, v]) => `${k}: ${String(v)}`).join(' · ') || t('people.noteOnly', { defaultValue: '(note only)' })}
              </p>
              {cr.note && <p className="text-[11px] text-ink-500 mt-0.5">“{cr.note}”</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => decide.mutate({ id: cr.id, decision: 'approved' })} disabled={decide.isPending}
                className="inline-flex h-8 items-center px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">{t('people.approve', { defaultValue: 'Approve' })}</button>
              <button onClick={() => decide.mutate({ id: cr.id, decision: 'rejected' })} disabled={decide.isPending}
                className="inline-flex h-8 items-center px-3 rounded-lg border border-ink-300 text-ink-700 text-xs font-semibold hover:bg-ink-100 disabled:opacity-50">{t('people.reject', { defaultValue: 'Reject' })}</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
